'use client'

import { useTranslations } from 'next-intl'

import { TRoomDetail } from '@/entities/room'
import { useFormatCurrency, useMultiScrollState } from '@/shared/lib'
import { Notice } from '@/shared/ui'

import { TabValue } from '../../lib/tab'
import { Heading, Item, Label, List, Value, Discounts } from '../common'

type Props = Pick<
  TRoomDetail,
  | 'earlyCheckinDiscounts'
  | 'longTermDiscounts'
  | 'usingFee'
  | 'mgmtFee'
  | 'cleanFee'
  | 'deposit'
>

export const MobileContractInformation = ({
  earlyCheckinDiscounts,
  longTermDiscounts,
  usingFee,
  mgmtFee,
  cleanFee,
  deposit,
}: Props) => {
  const t = useTranslations()

  const { formatCurrency } = useFormatCurrency()

  const { registerScrollElement } = useMultiScrollState<HTMLElement>()

  return (
    <section
      className="bg-gray-0 flex flex-col gap-4 px-4 py-10 md:flex-row"
      ref={element => {
        if (element) {
          registerScrollElement(TabValue.CONTRACT_INFORMATION, element)
        }
      }}
    >
      <Heading size="small" className="md:w-[263px]">
        {t('room_detail.lease_details.title')}
      </Heading>

      <div className="space-y-4 md:grow">
        <div className="rounded-sm bg-white p-5">
          <List spacing="small" className="typo-body2">
            <Item className="flex-col justify-start gap-[7px]">
              <div className="flex w-full items-center justify-between gap-2">
                <Label className="font-semibold">
                  {t('room_detail.lease_details.label.rent')}
                </Label>
                <Value>
                  {formatCurrency(usingFee)} / {t('common.week_unit')}
                </Value>
              </div>
              <Discounts
                earlyCheckinDiscounts={earlyCheckinDiscounts}
                longTermDiscounts={longTermDiscounts}
                highlightColor="gray"
                className="w-full"
              />
            </Item>
            <Item className="justify-between">
              <Label className="font-semibold">
                {t('room_detail.lease_details.label.utility_fee')}
              </Label>
              <Value>
                {formatCurrency(mgmtFee)} / {t('common.week_unit')}
              </Value>
            </Item>
            <Item className="justify-between">
              <Label className="font-semibold">
                {t('room_detail.lease_details.label.cleaning_fee')}
              </Label>
              <Value>{formatCurrency(cleanFee)}</Value>
            </Item>
            <Item className="justify-between">
              <Label className="font-semibold">
                {t('room_detail.lease_details.label.deposit')}
              </Label>
              <Value>{formatCurrency(deposit)}</Value>
            </Item>
          </List>
        </div>
        <Notice.Title className="mb-0 items-start font-normal">
          {t('room_detail.lease_details.notice')}
        </Notice.Title>
      </div>
    </section>
  )
}
